import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Cpu, Upload, AlertTriangle, Package, Calculator, Loader2,
  ChevronDown, ChevronRight, Shield, DollarSign, Clock, Search
} from "lucide-react";

const LifecyclePage = () => {
  const { t } = useTranslation('platform');
  const [boms, setBoms] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedBom, setSelectedBom] = useState(null);
  const [criticalView, setCriticalView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ltbCalc, setLtbCalc] = useState({ annual_demand: "", remaining_life_years: "", unit_cost: "", lead_time_days: "30", current_inventory: "0" });
  const [ltbResult, setLtbResult] = useState(null);
  const [filter, setFilter] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, sRes] = await Promise.all([
        fetch(`${API}/sentinel/lifecycle/bom?limit=50`, { credentials: "include" }),
        fetch(`${API}/sentinel/lifecycle/stats`, { credentials: "include" }),
      ]);
      if (bRes.ok) { const d = await bRes.json(); setBoms(d.boms || []); }
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uploadBOM = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/sentinel/lifecycle/bom/upload`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) { await fetchData(); }
      else { const err = await res.json(); alert(err.detail || "Upload failed"); }
    } catch (e) { console.error(e); }
    setUploading(false);
    e.target.value = "";
  };

  const viewBom = async (bomId) => {
    try {
      const res = await fetch(`${API}/sentinel/lifecycle/bom/${bomId}`, { credentials: "include" });
      if (res.ok) setSelectedBom(await res.json());
    } catch (e) { console.error(e); }
  };

  const viewCritical = async (bomId) => {
    try {
      const res = await fetch(`${API}/sentinel/lifecycle/bom/${bomId}/critical`, { credentials: "include" });
      if (res.ok) setCriticalView(await res.json());
    } catch (e) { console.error(e); }
  };

  const calcLTB = async () => {
    try {
      const res = await fetch(`${API}/sentinel/lifecycle/ltb-calculate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(Object.fromEntries(Object.entries(ltbCalc).map(([k, v]) => [k, parseFloat(v) || 0]))),
      });
      if (res.ok) setLtbResult(await res.json());
    } catch (e) { console.error(e); }
  };

  const riskBadge = (level) => {
    const styles = { critical: "bg-red-500/15 text-red-400 border-red-500/30", high: "bg-orange-500/15 text-orange-400 border-orange-500/30", medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
    return <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-[3px] border uppercase ${styles[level] || styles.low}`}>{level}</span>;
  };

  if (loading) return <QMSLayout title={t('platform:improvement.title')}><div className="flex justify-center h-64 items-center"><Loader2 className="w-8 h-8 animate-spin text-[#00C8E0]" /></div></QMSLayout>;

  return (
    <QMSLayout title={t('platform:improvement.title')}>
      <div className="space-y-6" data-testid="lifecycle-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>Component Lifecycle</h1>
            <p className="text-slate-400 text-sm mt-1">BOM scanning, YTEOL analysis, Last-Time-Buy calculations</p>
          </div>
          <label className="flex items-center gap-2 px-4 py-2 bg-[#1A6BCC] text-white text-xs font-bold uppercase tracking-wide rounded-[3px] cursor-pointer hover:shadow-[0_0_20px_rgba(0,200,224,0.3)] transition-all" data-testid="upload-bom-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? t('common:status.uploading') : t('operations:lifecycle.uploadBom')}
            <input type="file" accept=".csv,.json" className="hidden" onChange={uploadBOM} disabled={uploading} />
          </label>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Total BOMs", value: stats.total_boms, icon: Package },
              { label: "Components", value: stats.total_components, icon: Cpu },
              { label: "Critical", value: stats.critical, icon: AlertTriangle, color: "text-red-400" },
              { label: "High Risk", value: stats.high, icon: Shield, color: "text-orange-400" },
              { label: "LTB Cost", value: `$${(stats.total_ltb_cost || 0).toLocaleString()}`, icon: DollarSign, color: "text-cyan-400" },
            ].map((s, i) => (
              <div key={i} className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-4">
                <div className="flex items-center gap-2 mb-1"><s.icon className={`w-4 h-4 ${s.color || "text-slate-400"}`} /><span className="text-slate-500 text-xs">{s.label}</span></div>
                <span className={`text-xl font-bold ${s.color || "text-white"}`}>{s.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="bom-list">
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase mb-4 flex items-center gap-2" style={{ fontFamily: "'Syne', sans-serif" }}><Package className="w-5 h-5 text-[#00C8E0]" />BOM Uploads ({boms.length})</h3>
            {boms.length === 0 ? <p className="text-slate-500 text-xs">Upload a BOM CSV to start lifecycle analysis.</p> : (
              <div className="space-y-2">
                {boms.map((b) => (
                  <div key={b.bom_id} className="flex items-center justify-between p-3 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-mono">{b.bom_id}</span>
                        <span className="text-slate-400 text-xs truncate">{b.filename}</span>
                      </div>
                      <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-3">
                        <span>{b.component_count} components</span>
                        {b.critical_count > 0 && <span className="text-red-400 font-medium">{b.critical_count} critical</span>}
                        {b.warning_count > 0 && <span className="text-orange-400">{b.warning_count} warnings</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`view-bom-${b.bom_id}`} onClick={() => viewBom(b.bom_id)} className="px-3 py-1 text-xs text-slate-400 hover:text-[#00C8E0] border border-[#1E3A5F] rounded-[3px]">View All</button>
                      <button data-testid={`critical-${b.bom_id}`} onClick={() => viewCritical(b.bom_id)} className="px-3 py-1 text-xs text-red-400 hover:text-red-300 border border-red-500/20 rounded-[3px]">Critical</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="ltb-calculator">
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase mb-4 flex items-center gap-2" style={{ fontFamily: "'Syne', sans-serif" }}><Calculator className="w-5 h-5 text-[#00C8E0]" />LTB Calculator</h3>
            <div className="space-y-3">
              {Object.entries(ltbCalc).map(([k, v]) => (
                <div key={k}>
                  <label className="text-slate-400 text-xs block mb-1">{k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</label>
                  <input type="number" value={v} onChange={(e) => setLtbCalc({ ...ltbCalc, [k]: e.target.value })} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] focus:border-[#00C8E0] outline-none" data-testid={`ltb-${k}`} />
                </div>
              ))}
              <button data-testid="calc-ltb-btn" onClick={calcLTB} className="w-full py-2 bg-[#1A6BCC] text-white text-xs font-bold uppercase tracking-wide rounded-[3px] hover:shadow-[0_0_20px_rgba(0,200,224,0.3)] transition-all">Calculate LTB</button>
              {ltbResult && (
                <div className="p-3 bg-[#0F1E35] border border-[#00C8E0]/20 rounded-[3px] space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Safety Stock</span><span className="text-white font-medium">{ltbResult.safety_stock}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">LTB Quantity</span><span className="text-[#00C8E0] font-bold text-sm">{ltbResult.ltb_quantity}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">LTB Cost</span><span className="text-white font-medium">${ltbResult.ltb_cost?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Service Level</span><span className="text-slate-300">{ltbResult.service_level}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BOM Detail Modal */}
        {selectedBom && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedBom(null)}>
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] max-w-5xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bom-detail-modal">
              <div className="sticky top-0 bg-[#0F1E35] border-b border-[#1E3A5F] p-4 flex items-center justify-between z-10">
                <div><h2 className="text-white font-bold" style={{ fontFamily: "'Syne', sans-serif" }}>{selectedBom.bom_id}: {selectedBom.filename}</h2>
                  <span className="text-slate-400 text-xs">{selectedBom.component_count} components</span></div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-[#132340] rounded-[3px] px-3 py-1.5 border border-[#1E3A5F]"><Search className="w-3.5 h-3.5 text-slate-400" />
                    <input type="text" placeholder="Filter..." value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-transparent text-white text-xs outline-none w-32" data-testid="bom-filter" /></div>
                  <button onClick={() => setSelectedBom(null)} className="text-slate-400 hover:text-white text-xl">&times;</button>
                </div>
              </div>
              <div className="p-4">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="text-slate-400 border-b border-[#1E3A5F]">
                    <th className="text-left p-2">Part #</th><th className="text-left p-2">Manufacturer</th><th className="text-left p-2">YTEOL</th><th className="text-left p-2">Risk</th><th className="text-left p-2">Status</th><th className="text-right p-2">Safety Stock</th><th className="text-right p-2">LTB Qty</th><th className="text-right p-2">LTB Cost</th>
                  </tr></thead>
                  <tbody>
                    {(selectedBom.components || []).filter(c => !filter || c.part_number?.toLowerCase().includes(filter.toLowerCase()) || c.manufacturer?.toLowerCase().includes(filter.toLowerCase())).map((c, i) => (
                      <tr key={i} className="border-b border-[#1E3A5F]/50 hover:bg-[#132340]">
                        <td className="p-2 text-white font-mono">{c.part_number}</td>
                        <td className="p-2 text-slate-300">{c.manufacturer}</td>
                        <td className="p-2"><span className={c.yteol !== null && c.yteol <= 2 ? "text-red-400 font-bold" : "text-slate-300"}>{c.yteol !== null ? `${c.yteol} yr` : "N/A"}</span></td>
                        <td className="p-2">{riskBadge(c.risk_level)}</td>
                        <td className="p-2 text-slate-300 capitalize">{c.status?.replace(/_/g, " ")}</td>
                        <td className="p-2 text-right text-slate-300">{c.safety_stock}</td>
                        <td className="p-2 text-right text-[#00C8E0] font-medium">{c.ltb_quantity}</td>
                        <td className="p-2 text-right text-white">${c.ltb_cost?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Critical Components Modal */}
        {criticalView && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCriticalView(null)}>
            <div className="bg-[#0F1E35] border border-red-500/20 rounded-[3px] max-w-4xl w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="critical-modal">
              <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /><h2 className="text-red-400 font-bold">Critical Components — {criticalView.bom_id}</h2></div>
                <button onClick={() => setCriticalView(null)} className="text-slate-400 hover:text-white text-xl">&times;</button>
              </div>
              <div className="p-4">
                <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded-[3px] flex items-center gap-4 text-xs">
                  <span className="text-red-400 font-bold">{criticalView.critical_count} Critical</span>
                  <span className="text-white">Total LTB Cost: <strong className="text-red-400">${criticalView.total_ltb_cost?.toLocaleString()}</strong></span>
                </div>
                <div className="space-y-2">
                  {(criticalView.critical_components || []).map((c, i) => (
                    <div key={i} className="p-3 bg-[#132340] border border-red-500/10 rounded-[3px] flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2"><span className="text-white font-mono text-sm">{c.part_number}</span>{riskBadge(c.risk_level)}</div>
                        <div className="text-slate-400 text-xs mt-0.5">{c.manufacturer} | YTEOL: <span className="text-red-400 font-bold">{c.yteol !== null ? `${c.yteol} yr` : "EOL"}</span></div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#00C8E0] font-bold text-sm">LTB: {c.ltb_quantity} units</div>
                        <div className="text-slate-400 text-xs">${c.ltb_cost?.toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default LifecyclePage;
